import { useQuery } from '@tanstack/react-query';

import React, { useMemo, useState } from 'react';

import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import {
	ApiKeyAccessChannel,
	ApiKeyClient,
	ApiKeyDependencyModel,
	ApiKeyDependencyRequest,
	ComponentModel,
	ContextClient,
	ContextScopeModel,
	DeepOperationModel,
	OperationsClient,
	SetApiKeyDependenciesRequest,
} from '../../../api/api';
import { sortByName } from '../../../common/sortByName';

const TARGET_TYPE_OPERATION = 'Operation';
const TARGET_TYPE_SCOPE = 'Scope';

interface AddApiKeyDependencyDialogProps {
	open: boolean;
	apiKeyId: string;
	components: ComponentModel[];
	existingDependencies: ApiKeyDependencyModel[];
	onClose: () => void;
	onDependencyAdded: () => void;
}

export default function AddApiKeyDependencyDialog({
	open,
	apiKeyId,
	components,
	existingDependencies,
	onClose,
	onDependencyAdded,
}: AddApiKeyDependencyDialogProps) {
	const client = useApiClient(ApiKeyClient) as ApiKeyClient;
	const contextClient = useApiClient(ContextClient) as ContextClient;
	const operationsClient = useApiClient(OperationsClient) as OperationsClient;
	const toastService = useToastService();
	const [componentId, setComponentId] = useState('');
	const [accessChannel, setAccessChannel] = useState<ApiKeyAccessChannel>(ApiKeyAccessChannel.ApiAndMcp);
	const [selectedOperationIds, setSelectedOperationIds] = useState<string[]>([]);
	const [selectedScopeIds, setSelectedScopeIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	const selectedComponent = useMemo(() => components.find((component) => component.id === componentId), [components, componentId]);
	const existingTargetKeys = useMemo(
		() =>
			new Set(
				existingDependencies
					.filter((dependency) => dependency.dependsOnComponentId === selectedComponent?.id && dependency.accessChannel === accessChannel)
					.map((dependency) => `${dependency.targetType}:${dependency.targetId}`),
			),
		[accessChannel, existingDependencies, selectedComponent?.id],
	);
	const availableComponents = useMemo(() => components.filter((component) => !!component.contextId).sort(sortByName), [components]);

	const { data: contextData, isFetching: isFetchingContext } = useQuery({
		queryKey: ['api-key-dependency-context', selectedComponent?.contextId],
		queryFn: () => contextClient.getContextById(selectedComponent!.contextId!),
		enabled: open && !!selectedComponent?.contextId,
	});

	const { data: operations = [], isFetching: isFetchingOperations } = useQuery({
		queryKey: ['api-key-dependency-operations', selectedComponent?.contextId],
		queryFn: () => operationsClient.getOperationsForContext(selectedComponent!.contextId!),
		enabled: open && !!selectedComponent?.contextId,
	});

	const availableScopes = useMemo(
		() => [...(contextData?.scopes ?? [])].filter((scope) => !existingTargetKeys.has(`${TARGET_TYPE_SCOPE}:${scope.id}`)).sort(sortByName),
		[contextData?.scopes, existingTargetKeys],
	);
	const availableOperations = useMemo(
		() => operations.filter((operation) => !existingTargetKeys.has(`${TARGET_TYPE_OPERATION}:${operation.id}`)).sort(sortByName),
		[existingTargetKeys, operations],
	);
	const isLoadingOptions = isFetchingContext || isFetchingOperations;
	const hasSelection = selectedOperationIds.length > 0 || selectedScopeIds.length > 0;
	const canSave = !!selectedComponent && hasSelection;

	const resetSelection = () => {
		setComponentId('');
		setAccessChannel(ApiKeyAccessChannel.ApiAndMcp);
		setSelectedOperationIds([]);
		setSelectedScopeIds([]);
	};

	const handleClose = () => {
		if (isSaving) return;
		resetSelection();
		onClose();
	};

	const handleComponentChange = (nextComponentId: string) => {
		setComponentId(nextComponentId);
		setSelectedOperationIds([]);
		setSelectedScopeIds([]);
	};

	const handleAccessChannelChange = (_event: React.MouseEvent<HTMLElement>, nextChannel: ApiKeyAccessChannel | null) => {
		if (!nextChannel) return;
		setAccessChannel(nextChannel);
		setSelectedOperationIds([]);
		setSelectedScopeIds([]);
	};

	const handleSave = async () => {
		if (!canSave || !selectedComponent) return;

		setIsSaving(true);
		try {
			const dependencies: ApiKeyDependencyRequest[] = [
				...selectedOperationIds.map((operationId) => ({
					dependsOnComponentId: selectedComponent.id,
					targetId: operationId,
					targetType: TARGET_TYPE_OPERATION,
					accessChannel,
				})),
				...selectedScopeIds.map((scopeId) => ({
					dependsOnComponentId: selectedComponent.id,
					targetId: scopeId,
					targetType: TARGET_TYPE_SCOPE,
					accessChannel,
				})),
			];

			const request: SetApiKeyDependenciesRequest = {
				add: dependencies,
			};

			await client.setDependencies(apiKeyId, request);
			toastService.success(`Added ${dependencies.length} API key ${dependencies.length === 1 ? 'dependency' : 'dependencies'} for ${selectedComponent.name}.`);
			resetSelection();
			onDependencyAdded();
		} catch (error) {
			console.error('Error adding API key dependency:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to add API key dependency: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Add API Key Access</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<FormControl fullWidth>
						<InputLabel id="api-key-dependency-component-label">Component</InputLabel>
						<Select
							labelId="api-key-dependency-component-label"
							label="Component"
							value={componentId}
							onChange={(event) => handleComponentChange(event.target.value)}
						>
							{availableComponents.map((component) => (
								<MenuItem key={component.id} value={component.id}>
									{component.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Stack spacing={0.75}>
						<Typography variant="subtitle2">Access Area</Typography>
						<ToggleButtonGroup exclusive size="small" value={accessChannel} onChange={handleAccessChannelChange} color="primary">
							<ToggleButton value={ApiKeyAccessChannel.Api}>API</ToggleButton>
							<ToggleButton value={ApiKeyAccessChannel.Mcp}>MCP</ToggleButton>
							<ToggleButton value={ApiKeyAccessChannel.ApiAndMcp}>Both</ToggleButton>
						</ToggleButtonGroup>
					</Stack>

					<FormControl fullWidth disabled={!selectedComponent || isLoadingOptions}>
						<InputLabel id="api-key-dependency-scopes-label">Scopes</InputLabel>
						<Select
							labelId="api-key-dependency-scopes-label"
							label="Scopes"
							multiple
							value={selectedScopeIds}
							onChange={(event) => setSelectedScopeIds(normalizeSelectedIds(event.target.value))}
							renderValue={(selected) => renderScopeNames(selected as string[], availableScopes)}
						>
							{availableScopes.map((scope) => (
								<MenuItem key={scope.id} value={scope.id}>
									<Checkbox checked={selectedScopeIds.includes(scope.id)} />
									<ListItemText primary={scope.name} secondary={scope.description} />
								</MenuItem>
							))}
							{availableScopes.length === 0 && (
								<MenuItem disabled>
									<ListItemText primary="No additional scopes available for this access area" />
								</MenuItem>
							)}
						</Select>
					</FormControl>

					<FormControl fullWidth disabled={!selectedComponent || isLoadingOptions}>
						<InputLabel id="api-key-dependency-operations-label">Operations</InputLabel>
						<Select
							labelId="api-key-dependency-operations-label"
							label="Operations"
							multiple
							value={selectedOperationIds}
							onChange={(event) => setSelectedOperationIds(normalizeSelectedIds(event.target.value))}
							renderValue={(selected) => renderOperationNames(selected as string[], availableOperations)}
						>
							{availableOperations.map((operation) => (
								<MenuItem key={operation.id} value={operation.id}>
									<Checkbox checked={selectedOperationIds.includes(operation.id)} />
									<ListItemText primary={operation.name} secondary={operation.description} />
								</MenuItem>
							))}
							{availableOperations.length === 0 && (
								<MenuItem disabled>
									<ListItemText primary="No additional operations available for this access area" />
								</MenuItem>
							)}
						</Select>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={isSaving || !canSave} variant="contained">
					{isSaving ? 'Saving...' : 'Add'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function renderScopeNames(selectedIds: string[], scopes: ContextScopeModel[]) {
	const scopeNameById = new Map(scopes.map((scope) => [scope.id, scope.name]));
	return selectedIds.map((id) => scopeNameById.get(id) ?? id).join(', ');
}

function renderOperationNames(selectedIds: string[], operations: DeepOperationModel[]) {
	const operationNameById = new Map(operations.map((operation) => [operation.id, operation.name]));
	return selectedIds.map((id) => operationNameById.get(id) ?? id).join(', ');
}

function normalizeSelectedIds(value: string | string[]) {
	return typeof value === 'string' ? value.split(',') : value;
}
